// PeopleCell.tsx — „Beteiligte Personen" als EINE Spalte (A11/A12): gestapelte
// Kreise mit Entra-Profilfoto (Fallback Kürzel/Initialen, Schrift skaliert bei
// langen Kürzeln herunter). Klick öffnet ein Popover mit der Aufschlüsselung nach
// Rolle: Hauptverantwortlich · Weitere Bearbeiter · Zu informieren.

import * as React from "react";
import { InitialsAvatar } from "../../chat/chatUi";
import { RdPopover } from "../../redesign/table/RdPopover";

/** Eine für die Avatar-Anzeige aufgelöste Person (inkl. kanonischer User-Id fürs Foto). */
export interface PersonAvatar {
  /** Roh-Key (id/email/kuerzel) — stabil für React + Dedupe. */
  key: string;
  name: string;
  /** Kürzel bzw. Namens-Initialen (≤4 Zeichen). */
  initials: string;
  /** Kanonische User-Id → Entra-Profilfoto `/users/:id/photo`; null = nur Initialen. */
  userId: string | null;
}

export interface PeopleGroups {
  hauptverantwortlich: PersonAvatar[];
  bearbeiter: PersonAvatar[];
  zuInformierende: PersonAvatar[];
}

/** Dedupliziert die drei Rollen-Listen zur Anzeige-Union (Reihenfolge HV → Bearbeiter → Info). */
export function unionPeople(groups: PeopleGroups): PersonAvatar[] {
  const seen = new Set<string>();
  const out: PersonAvatar[] = [];
  for (const p of [...groups.hauptverantwortlich, ...groups.bearbeiter, ...groups.zuInformierende]) {
    if (seen.has(p.key)) continue;
    seen.add(p.key);
    out.push(p);
  }
  return out;
}

const RING: React.CSSProperties = {
  borderRadius: "50%",
  boxShadow: "0 0 0 2px #fff",
  background: "#fff",
};

/** Ein einzelner Avatar-Kreis (Foto oder Kürzel). */
const Circle: React.FC<{ p: PersonAvatar; size?: number }> = ({ p, size = 28 }) => (
  <span style={{ ...RING, display: "inline-flex", flexShrink: 0 }} title={p.name}>
    <InitialsAvatar name={p.name} size={size} userId={p.userId} label={p.initials || undefined} />
  </span>
);

const MAX_INLINE = 4;

/** Popover-Gruppe: Titel + Zeilen (Avatar + voller Name). Leere Gruppen entfallen. */
const Group: React.FC<{ title: string; people: PersonAvatar[] }> = ({ title, people }) => {
  if (people.length === 0) return null;
  return (
    <div>
      <div className="rd-pop-grouptitle">{title}</div>
      {people.map((p) => (
        <div key={p.key} style={{ display: "flex", alignItems: "center", gap: 9, padding: "5px 4px" }}>
          <InitialsAvatar name={p.name} size={24} userId={p.userId} label={p.initials || undefined} />
          <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13.5, color: "#241522" }}>
            {p.name}
          </span>
        </div>
      ))}
    </div>
  );
};

/**
 * „Beteiligte Personen"-Zelle. Leere Beteiligung → leere Zelle (kein „—").
 * Klick auf die Avatare öffnet die rollenbasierte Aufschlüsselung.
 */
export const PeopleCell: React.FC<{ groups: PeopleGroups }> = ({ groups }) => {
  const all = React.useMemo(() => unionPeople(groups), [groups]);
  if (all.length === 0) return null;

  const inline = all.slice(0, MAX_INLINE);
  const overflow = all.length - inline.length;

  return (
    <RdPopover
      align="start"
      width={244}
      trigger={({ toggle, ref }) => (
        <button
          ref={ref}
          type="button"
          aria-label="Beteiligte anzeigen"
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            border: "none",
            background: "transparent",
            cursor: "pointer",
            padding: 0,
          }}
        >
          {inline.map((p, i) => (
            <span key={p.key} style={{ marginLeft: i === 0 ? 0 : -9 }}>
              <Circle p={p} />
            </span>
          ))}
          {overflow > 0 && (
            <span
              style={{
                ...RING,
                marginLeft: -9,
                width: 28,
                height: 28,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                fontWeight: 800,
                color: "#7d2169",
                backgroundColor: "#f0dcea",
              }}
            >
              +{overflow}
            </span>
          )}
        </button>
      )}
    >
      {() => (
        <div className="rd-pop-body" onClick={(e) => e.stopPropagation()}>
          <Group title="Hauptverantwortlich" people={groups.hauptverantwortlich} />
          <Group title="Weitere Bearbeiter" people={groups.bearbeiter} />
          <Group title="Zu informieren" people={groups.zuInformierende} />
        </div>
      )}
    </RdPopover>
  );
};
